import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  TaxDeclaration, SubmitDeclarationPayload, UploadProofPayload, ReviewDeclarationPayload,
} from './types';

/** The caller's own declaration for a financial year (or another employee's, if HR/admin). */
export function useTaxDeclaration(financialYear: string, userId?: string) {
  return useQuery({
    queryKey: ['hr', 'tax-declaration', financialYear, userId ?? 'self'],
    enabled: Boolean(financialYear),
    queryFn: async () => {
      const params = new URLSearchParams({ financialYear });
      if (userId) params.set('userId', userId);
      const { data } = await api.get<{ declaration: TaxDeclaration | null }>(`/api/hr/tax/declarations?${params}`);
      return data.declaration;
    },
    placeholderData: keepPreviousData,
  });
}

/** HR-admin review queue across all employees. */
export function useTaxDeclarationsSummary(financialYear?: string, status?: string) {
  return useQuery({
    queryKey: ['hr', 'tax-declarations-summary', financialYear, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (financialYear) params.set('financialYear', financialYear);
      if (status) params.set('status', status);
      const { data } = await api.get<{ declarations: TaxDeclaration[] }>(`/api/hr/tax/declarations/summary?${params}`);
      return data.declarations ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useSubmitDeclaration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SubmitDeclarationPayload) => {
      const { data } = await api.post<{ declaration: TaxDeclaration }>('/api/hr/tax/declarations', payload);
      return data.declaration;
    },
    onSuccess: (declaration) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'tax-declaration', declaration.financialYear] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'tax-declarations-summary'] });
    },
  });
}

export function useSubmitForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (declarationId: string) => {
      const { data } = await api.post<{ declaration: TaxDeclaration }>(`/api/hr/tax/declarations/${declarationId}/submit`);
      return data.declaration;
    },
    onSuccess: (declaration) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'tax-declaration', declaration.financialYear] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'tax-declarations-summary'] });
    },
  });
}

export function useUploadProof(declarationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UploadProofPayload) => {
      const { data } = await api.post<{ declaration: TaxDeclaration }>(`/api/hr/tax/declarations/${declarationId}/proofs`, payload);
      return data.declaration;
    },
    onSuccess: (declaration) => queryClient.invalidateQueries({ queryKey: ['hr', 'tax-declaration', declaration.financialYear] }),
  });
}

export function useReviewDeclaration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: ReviewDeclarationPayload & { id: string }) => {
      const { data } = await api.patch<{ declaration: TaxDeclaration }>(`/api/hr/tax/declarations/${id}/review`, payload);
      return data.declaration;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'tax-declarations-summary'] });
      queryClient.invalidateQueries({ queryKey: ['hr', 'tax-declaration'] });
    },
  });
}
